import Reveal from "./Reveal";
import { Skeleton } from "./Skeleton";

// SVG circular progress — full-circle track, arc filled to value, % centered.
//
// While `loading`, the ring itself is real: the track draws in full and the arc
// sits at zero. Only the figures are placeholders. When the value arrives the
// same <circle> is still mounted, so `anim-arc` sweeps it from 0 to its reading
// instead of a ghost being swapped out for a finished chart.
export default function ProgressRing({
  value,
  max,
  label,
  color = "var(--accent)",
  size = 120,
  loading = false,
}: {
  value: number;
  max: number;
  label: string;
  color?: string;
  size?: number;
  loading?: boolean;
}) {
  const sw = 10;
  const c2 = size / 2;
  const r = c2 - sw / 2 - 1;
  const c = 2 * Math.PI * r;
  const frac = loading ? 0 : Math.min(Math.max(value / max, 0), 1);
  const percent = frac * 100;

  return (
    <Reveal>
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} className="-rotate-90">
          <circle cx={c2} cy={c2} r={r} fill="none" stroke="var(--neu-dark)" strokeWidth={sw} opacity={0.25} />
          <circle
            className="anim-arc"
            cx={c2}
            cy={c2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={sw}
            strokeLinecap="round"
            strokeDasharray={`${frac * c} ${c - frac * c}`}
          />
          {!loading && (
            <text
              x={c2}
              y={c2 + 6}
              textAnchor="middle"
              fontSize={size * 0.17}
              fontWeight="600"
              fill="var(--text)"
              transform={`rotate(90 ${c2} ${c2})`}
            >
              {percent.toFixed(1)}%
            </text>
          )}
        </svg>
        {/* Centred over the ring so the reading and its placeholder occupy the
            same spot — no shift when the text replaces the skeleton. */}
        {loading && (
          <div className="absolute inset-0 grid place-items-center">
            <Skeleton className="h-5 w-14 rounded" />
          </div>
        )}
      </div>
      {/* The caption is static copy, not a reading — it renders for real even
          while loading. */}
      <p className="text-[11.5px] text-text-faint mt-1.5">{label}</p>
    </div>
    </Reveal>
  );
}
